import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    Paper,
    Stack,
    InputAdornment,
    IconButton,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Tooltip,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import PercentIcon from '@mui/icons-material/Percent';
import { useForm, Controller } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { request } from '../../../../utils/request';
import { toast } from 'react-toastify';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import PriceCodeDisplay from '../../../controlPanel/components/PriceCodeDisplay.tsx';
import { useDiscountCalculator } from '../../../../utils/useDiscountCalculator';
import { AttributeField, Brand, CarModel, Category, DescriptionData, ProductFormData, SelectedCompatibility, Vendor } from '../../types';
import { baseUrlImage } from '../../../../utils/apiClient';
import { useImageCompression } from '../../../../utils/useImageCompression';

const EditProduct: React.FC = () => {
    const { compressMultipleImages, isCompressing } = useImageCompression();
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loadingProduct, setLoadingProduct] = useState(true);
    const [categories, setCategories] = useState<Category[]>([]);
    const [, setAllBrands] = useState<Brand[]>([]);
    const [carBrands, setCarBrands] = useState<Brand[]>([]);
    const [partBrands, setPartBrands] = useState<Brand[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [carModels, setCarModels] = useState<CarModel[]>([]);
    const [filteredCarModels, setFilteredCarModels] = useState<CarModel[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [attributeFields, setAttributeFields] = useState<AttributeField[]>([]);
    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [existingImages, setExistingImages] = useState<string[]>([]);
    const [selectedCompatibility, setSelectedCompatibility] = useState<SelectedCompatibility[]>([]);
    const [selectedCarBrand, setSelectedCarBrand] = useState<Brand | null>(null);
    const [selectedCarModel, setSelectedCarModel] = useState<CarModel | null>(null);
    const [selectedVariants, setSelectedVariants] = useState<string[]>([]);
    const [selectedFuelTypes, setSelectedFuelTypes] = useState<string[]>([]);
    const [selectedTransmissions, setSelectedTransmissions] = useState<string[]>([]);
    const [selectedGenerations, setSelectedGenerations] = useState<string[]>([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingCompatibilityIndex, setEditingCompatibilityIndex] = useState<number | null>(null);
    const [brandSearchInput, setBrandSearchInput] = useState('');
    const [modelSearchInput, setModelSearchInput] = useState('');
    const [categorySearchInput, setCategorySearchInput] = useState('');
    const [vendorSearchInput, setVendorSearchInput] = useState('');

    const [descriptionJsonFields, setDescriptionJsonFields] = useState<Array<{ key: string; value: string }>>([]);
    const [newJsonField, setNewJsonField] = useState<{ key: string, value: string }>({ key: "", value: "" });

    // Add these state variables after the existing ones
    const [sourceType, setSourceType] = useState<'manual' | 'price-list' | 'import' | 'api'>('manual');
    const [sourceId, setSourceId] = useState('');
    const [importBatchId, setImportBatchId] = useState('');
    const [sourceMetadata, setSourceMetadata] = useState<Array<{ key: string; value: string }>>([]);
    // const [newSourceMetadata, setNewSourceMetadata] = useState<{ key: string; value: string }>({ key: "", value: "" });

    const { calculatePrice, calculateUnitPrice } = useDiscountCalculator();

    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm<ProductFormData>({
        defaultValues: {
            name: '',
            partNo: '',
            barcode: '',
            productImages: [],
            quantity: 0,
            category: '',
            brand: '',
            vender: '',
            mrp: "",
            unitPrice: "",
            purchaseDiscount: "",
            purchasePrice: "",
            discountB2C: "",
            sellingPriceB2C: "",
            discountB2B: "",
            sellingPriceB2B: "",
            description: {
                text: "",
                jsonFields: {}
            },
            compatibility: [],
            attributes: {},
            status: 'active',
            source: {
                type: 'manual',
                id: '',
                date: new Date(),
                metadata: {}
            },
            importBatchId: ''
        },
    });

    const watchedCategory = watch('category');

    // calculate purchase price
    const mrpValue = watch('mrp');
    const purchaseDiscountValue = watch('purchaseDiscount');
    const discountB2CValue = watch('discountB2C');
    const discountB2BValue = watch('discountB2B');

    useEffect(() => {
        // Convert to numbers, handle empty strings
        const mrpNum = mrpValue ? Number(mrpValue) : 0;
        const discountNum = purchaseDiscountValue ? Number(purchaseDiscountValue) : 0;
        const discountB2CNum = discountB2CValue ? Number(discountB2CValue) : 0;
        const discountB2BNum = discountB2BValue ? Number(discountB2BValue) : 0;

        // Only calculate if MRP is provided and valid
        if (mrpNum > 0) {
            const calculatedPrice = calculatePrice(mrpNum, discountNum);
            const calculatedB2CPrice = calculatePrice(mrpNum, discountB2CNum);
            const calculatedB2BPrice = calculatePrice(mrpNum, discountB2BNum);
            const calculatedUnitPrice = calculateUnitPrice(mrpNum);
            setValue('purchasePrice', calculatedPrice);
            setValue('sellingPriceB2C', calculatedB2CPrice);
            setValue('sellingPriceB2B', calculatedB2BPrice);
            setValue('unitPrice', calculatedUnitPrice);
        } else if (discountNum > 0 && !mrpValue) {
            setValue('purchasePrice', 0);
        } else if (discountB2CNum > 0 && !mrpValue) {
            setValue('sellingPriceB2C', 0);
        } else if (discountB2BNum > 0 && !mrpValue) {
            setValue('sellingPriceB2B', 0);
        }
    }, [calculatePrice, calculateUnitPrice, setValue, mrpValue, purchaseDiscountValue, discountB2CValue, discountB2BValue]);

    // Fetch product data and form data on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoadingProduct(true);

                // Fetch form data first (categories, brands, vendors, car models)
                await fetchFormData();

                // Then fetch product data
                if (id) {
                    const productRes = await request({
                        url: `product/get-one-product/${id}`,
                        method: 'get',
                    });

                    if (productRes.status === 'success' && productRes.product) {
                        const product = productRes.product;

                        // Parse description - handle both old string and new object format
                        let descriptionData: DescriptionData = { text: '', jsonFields: {} };

                        if (typeof product.description === 'string') {
                            // Old format: string description
                            descriptionData.text = product.description;
                        } else if (product.description && typeof product.description === 'object') {
                            // New format: object with text and jsonFields
                            descriptionData = {
                                text: product.description.text || '',
                                jsonFields: product.description.jsonFields || {}
                            };

                            // Load jsonFields into state
                            if (product.description.jsonFields) {
                                const fields = Object.entries(product.description.jsonFields).map(([key, value]) => ({
                                    key,
                                    value: String(value)
                                }));
                                setDescriptionJsonFields(fields);
                            }
                        }

                        // Set source and importBatchId
                        if (product.source) {
                            setSourceType(product.source.type || 'manual');
                            setSourceId(product.source.id || '');

                            // Load source metadata
                            if (product.source.metadata) {
                                const fields = Object.entries(product.source.metadata).map(([key, value]) => ({
                                    key,
                                    value: String(value)
                                }));
                                setSourceMetadata(fields);
                            }
                        }

                        if (product.importBatchId) {
                            setImportBatchId(product.importBatchId);
                        }

                        // Set form values
                        reset({
                            name: product.name,
                            partNo: product.partNo,
                            barcode: product.barcode || '',
                            productImages: product.productImages || [],
                            quantity: product.quantity,
                            category: product.category,
                            brand: product.brand,
                            vender: product.vender,
                            mrp: product.mrp,
                            unitPrice: product.unitPrice,
                            purchaseDiscount: product.purchaseDiscount || "",
                            purchasePrice: product.purchasePrice,
                            discountB2C: product.discountB2C || "",
                            sellingPriceB2C: product.sellingPriceB2C,
                            discountB2B: product.discountB2B || "",
                            sellingPriceB2B: product.sellingPriceB2B,
                            description: descriptionData,
                            status: product.status,
                            attributes: product.attributes || {},
                            compatibility: product.compatibility || [],
                            source: product.source || {
                                type: 'manual',
                                id: '',
                                date: new Date(),
                                metadata: {}
                            },
                            importBatchId: product.importBatchId || ''
                        });

                        // Set existing images
                        setExistingImages(product.productImages || []);

                        // Set compatibility data
                        if (product.compatibility && product.compatibility.length > 0) {
                            const compData: SelectedCompatibility[] = product.compatibility.map((comp: any) => ({
                                carModel: comp._id || comp.name,
                                carName: comp.name,
                                brandName: comp.brand?.name || '',
                                baseImage: comp.baseImage || comp.brand?.logo || '',
                                variants: comp.variants || [],
                                fuelTypes: comp.fuelTypes || [],
                                transmissions: comp.transmissions || [],
                                generations: comp.generations?.map((gen: any) => {
                                    if (gen.from && gen.to) {
                                        return `${gen.from}-${gen.to}`;
                                    }
                                    return '';
                                }).filter((gen: string) => gen) || [],
                            }));
                            setSelectedCompatibility(compData);
                        }
                    } else {
                        toast.error('Product not found');
                        navigate('/inventory', { replace: true });
                        return;
                    }
                }

            } catch (error: any) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load form data');
            } finally {
                setLoadingProduct(false);
            }
        };

        fetchData();
    }, [id, navigate, reset]);

    // Fetch form data (categories, brands, vendors, car models)
    const fetchFormData = async () => {
        try {
            // Fetch categories
            const categoriesRes = await request({
                url: 'category/get-all-category',
                method: 'get',
            });
            if (categoriesRes.status === 'success') {
                setCategories(categoriesRes.categories || []);
            }

            // Fetch all brands
            setLoadingBrands(true);
            const brandsRes = await request({
                url: 'brand/get-all-brands',
                method: 'get',
            });

            let brandsList: Brand[] = [];
            if (brandsRes.brands) {
                brandsList = brandsRes.brands;
            } else if (brandsRes.data) {
                brandsList = brandsRes.data;
            } else if (Array.isArray(brandsRes)) {
                brandsList = brandsRes;
            }

            setAllBrands(brandsList);

            // Filter brands by manufactureType
            const carBrandsList = brandsList.filter(brand =>
                brand.manufactureType.includes('Cars')
            );
            const partBrandsList = brandsList.filter(brand =>
                brand.manufactureType.includes('Parts')
            );

            setCarBrands(carBrandsList);
            setPartBrands(partBrandsList);
            setLoadingBrands(false);

            // Fetch vendors
            setLoadingVendors(true);
            const vendorsRes = await request({
                url: 'vendor/get-all-vendors',
                method: 'get',
            });

            let vendorsList: Vendor[] = [];
            if (vendorsRes.vendors) {
                vendorsList = vendorsRes.vendors;
            } else if (vendorsRes.data) {
                vendorsList = vendorsRes.data;
            } else if (Array.isArray(vendorsRes)) {
                vendorsList = vendorsRes;
            }

            setVendors(vendorsList || []);
            setLoadingVendors(false);

            // Fetch car models for compatibility
            setLoadingModels(true);
            const carModelsRes = await request({
                url: 'compatibility/get-all-car-models',
                method: 'get',
            });

            if (carModelsRes.models) {
                const models = (carModelsRes.models || []) as CarModel[];
                setCarModels(models);
            } else if (carModelsRes.data) {
                const models = (carModelsRes.data || []) as CarModel[];
                setCarModels(models);
            } else if (Array.isArray(carModelsRes)) {
                setCarModels(carModelsRes);
            } else {
                console.warn('Unexpected car models response structure:', carModelsRes);
                setCarModels([]);
            }
            setLoadingModels(false);

        } catch (error) {
            console.error('Error fetching form data:', error);
            toast.error('Failed to load form data');
            setLoadingBrands(false);
            setLoadingVendors(false);
            setLoadingModels(false);
        }
    };

    // Filter car models when brand is selected
    useEffect(() => {
        if (selectedCarBrand && carModels.length > 0) {
            const filtered = carModels.filter(model => {
                if (model.brand && model.brand._id === selectedCarBrand._id) {
                    return true;
                }
                if (typeof model.brand === 'string' && model.brand === selectedCarBrand._id) {
                    return true;
                }
                if (selectedCarBrand && model.brand && model.brand.name === selectedCarBrand.name) {
                    return true;
                }
                return false;
            });

            setFilteredCarModels(filtered);
            setSelectedCarModel(null);
        } else {
            setFilteredCarModels([]);
            setSelectedCarModel(null);
        }
    }, [selectedCarBrand, carModels]);

    // Reset compatibility selections when car model changes
    useEffect(() => {
        if (selectedCarModel) {
            setSelectedVariants([]);
            setSelectedFuelTypes([]);
            setSelectedTransmissions([]);
            setSelectedGenerations([]);
        }
    }, [selectedCarModel]);

    // Update attribute fields when category changes
    useEffect(() => {
        const currentAttributes = watch('attributes') || {};
        if (watchedCategory) {
            const category = categories.find(cat => cat.name === watchedCategory);
            setSelectedCategory(category || null);

            if (category && category.attributesTemplate) {
                const fields = category.attributesTemplate.map(attr => ({
                    ...attr,
                    value: currentAttributes[attr.key] !== undefined ? currentAttributes[attr.key] :
                        attr.type === 'boolean' ? false : attr.type === 'number' ? 0 : ''
                }));
                setAttributeFields(fields);
            } else {
                setAttributeFields([]);
            }
        } else {
            setSelectedCategory(null);
            setAttributeFields([]);
        }
    }, [watchedCategory, categories, watch]);

    const onSubmit = async (data: ProductFormData) => {
        try {
            setLoading(true);

            // Prepare attributes from form fields
            const attributes: Record<string, any> = {};
            attributeFields.forEach(field => {
                if (field.value !== '' && field.value !== null && field.value !== undefined) {
                    attributes[field.key] = field.value;
                }
            });

            // Convert descriptionJsonFields to object
            const jsonFieldsObj: Record<string, any> = {};
            descriptionJsonFields.forEach(field => {
                jsonFieldsObj[field.key] = field.value;
            });

            // Prepare description data
            const descriptionData = {
                text: data.description.text || '',
                jsonFields: jsonFieldsObj
            };

            // Prepare source metadata
            const metadataObj: Record<string, any> = {};
            sourceMetadata.forEach(field => {
                metadataObj[field.key] = field.value;
            });

            // Prepare source object
            const sourceData = {
                type: sourceType,
                id: sourceId || undefined,
                date: new Date(), // Update date when editing
                metadata: metadataObj
            };

            // Prepare compatibility data
            const compatibilityData = selectedCompatibility.map(comp => {
                const selectedModel = carModels.find(model => model._id === comp.carModel);
                const generations = comp.generations.map(genStr => {
                    const [from, to] = genStr.split('-');
                    return {
                        from: from || '',
                        to: to || '',
                        images: []
                    };
                });

                return {
                    name: comp.carName,
                    brand: {
                        name: comp.brandName,
                        logo: selectedModel?.baseImage || comp.baseImage || '',
                        parentCompany: ''
                    },
                    baseImage: comp.baseImage || '',
                    variants: comp.variants,
                    fuelTypes: comp.fuelTypes,
                    transmissions: comp.transmissions,
                    generations: generations
                };
            });

            const formData = new FormData();

            // Append basic fields
            formData.append('name', data.name);
            formData.append('partNo', data.partNo);
            formData.append('barcode', data.barcode || '');
            formData.append('quantity', data.quantity.toString());
            formData.append('category', data.category);
            formData.append('brand', data.brand);
            formData.append('vender', data.vender);
            formData.append('mrp', data.mrp.toString());
            formData.append('unitPrice', data.unitPrice.toString());
            formData.append('purchaseDiscount', data.purchaseDiscount.toString());
            formData.append('purchasePrice', data.purchasePrice.toString());
            formData.append('discountB2C', data.discountB2C.toString());
            formData.append('sellingPriceB2C', data.sellingPriceB2C.toString());
            formData.append('discountB2B', data.discountB2B.toString());
            formData.append('sellingPriceB2B', data.sellingPriceB2B.toString());
            formData.append('status', data.status);

            // Append source and import data
            formData.append('source', JSON.stringify(sourceData));
            if (importBatchId.trim()) {
                formData.append('importBatchId', importBatchId.trim());
            }

            // Append description as JSON string
            formData.append('description', JSON.stringify(descriptionData));

            // Send ONLY the remaining existing images
            formData.append('productImages', JSON.stringify(existingImages));

            // Append arrays and objects as JSON strings
            formData.append('compatibility', JSON.stringify(compatibilityData));
            formData.append('attributes', JSON.stringify(attributes));

            // Append new uploaded files
            imageFiles.forEach(file => {
                formData.append('productImages', file);
            });

            const res = await request({
                url: `product/update-product/${id}`,
                method: 'put',
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (res.status === 'success') {
                toast.success(res.message);
                navigate(`/inventory/${res.product._id}`, { replace: true });
            }
        } catch (error: any) {
            console.error('Error updating product:', error);
            toast.error(error.message || 'Failed to update product');
        } finally {
            setLoading(false);
        }
    };

    // const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     const files = Array.from(event.target.files || []);
    //     if (files.length + imageFiles.length + existingImages.length > 5) {
    //         toast.error('Maximum 5 images allowed');
    //         return;
    //     }

    //     setImageFiles(prev => [...prev, ...files]);

    //     // Create preview URLs
    //     files.forEach(file => {
    //         const reader = new FileReader();
    //         reader.onload = (e) => {
    //             setImagePreviews(prev => [...prev, e.target?.result as string]);
    //         };
    //         reader.readAsDataURL(file);
    //     });
    // };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        const totalImages = existingImages.length + imageFiles.length + files.length;

        if (totalImages > 5) {
            toast.error(`Maximum 5 images allowed. You already have ${existingImages.length + imageFiles.length} images.`);
            return;
        }

        // Validate file sizes and types
        const invalidFiles = files.filter(f => f.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            toast.error('Some images exceed 10MB limit');
            return;
        }

        const invalidTypes = files.filter(f => !f.type.startsWith('image/'));
        if (invalidTypes.length > 0) {
            toast.error('Please upload only image files');
            return;
        }

        try {
            // Compress all images
            const compressedFiles = await compressMultipleImages(files);

            setImageFiles(prev => [...prev, ...compressedFiles]);

            // Create preview URLs for compressed images
            compressedFiles.forEach(file => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setImagePreviews(prev => [...prev, e.target?.result as string]);
                };
                reader.readAsDataURL(file);
            });

            toast.success(`${compressedFiles.length} image(s) uploaded successfully`);
        } catch (error) {
            console.error('Compression error:', error);
            toast.error('Failed to compress images. Please try again.');

            // Fallback: add original files if compression fails
            setImageFiles(prev => [...prev, ...files]);
            files.forEach(file => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setImagePreviews(prev => [...prev, e.target?.result as string]);
                };
                reader.readAsDataURL(file);
            });
        }

        // Reset input to allow re-uploading same files
        event.target.value = '';
    };

    const handleRemoveImage = (index: number, type: 'new' | 'existing') => {
        if (type === 'new') {
            setImageFiles(prev => prev.filter((_, i) => i !== index));
            setImagePreviews(prev => prev.filter((_, i) => i !== index));
        } else {
            setExistingImages(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleAttributeChange = (index: number, value: any) => {
        const updatedFields = [...attributeFields];
        updatedFields[index].value = value;
        setAttributeFields(updatedFields);
    };

    const handleAddCompatibility = () => {
        if (!selectedCarModel) {
            toast.error('Please select a car model');
            return;
        }

        const brandName = selectedCarBrand?.name || (selectedCarModel.brand && selectedCarModel.brand.name) || 'Unknown Brand';

        const generationStrings = selectedGenerations.map(gen => {
            const [from, to] = gen.split('-');
            return `${from}-${to}`;
        });

        const compatibility: SelectedCompatibility = {
            carModel: selectedCarModel._id,
            carName: selectedCarModel.name,
            brandName: brandName,
            baseImage: selectedCarModel.baseImage || '',
            variants: selectedVariants,
            fuelTypes: selectedFuelTypes,
            transmissions: selectedTransmissions,
            generations: generationStrings,
        };

        setSelectedCompatibility(prev => [...prev, compatibility]);

        // Reset all selections
        setSelectedCarBrand(null);
        setSelectedCarModel(null);
        setSelectedVariants([]);
        setSelectedFuelTypes([]);
        setSelectedTransmissions([]);
        setSelectedGenerations([]);
        setBrandSearchInput('');
        setModelSearchInput('');

        toast.success('Vehicle compatibility added successfully!');
    };

    const handleEditCompatibility = (index: number) => {
        const comp = selectedCompatibility[index];

        // Find the brand object
        const brand = carBrands.find(b => b.name === comp.brandName);
        setSelectedCarBrand(brand || null);

        // After setting brand, wait for filteredCarModels to update
        setTimeout(() => {
            // Find the model in filteredCarModels
            const model = filteredCarModels.find(m =>
                m._id === comp.carModel ||
                m.name === comp.carName
            );

            if (model) {
                setSelectedCarModel(model);
                setSelectedVariants(comp.variants || []);
                setSelectedFuelTypes(comp.fuelTypes || []);
                setSelectedTransmissions(comp.transmissions || []);
                setSelectedGenerations(comp.generations || []);
            } else {
                toast.error('Car model not found in database');
            }

            setEditingCompatibilityIndex(index);
            setEditDialogOpen(true);
        }, 100);
    };

    const handleUpdateCompatibility = () => {
        if (!selectedCarModel || editingCompatibilityIndex === null) {
            toast.error('Please select a car model');
            return;
        }

        const brandName = selectedCarBrand?.name || (selectedCarModel.brand && selectedCarModel.brand.name) || 'Unknown Brand';

        const generationStrings = selectedGenerations.map(gen => {
            const [from, to] = gen.split('-');
            return `${from}-${to}`;
        });

        const updatedCompatibility: SelectedCompatibility = {
            carModel: selectedCarModel._id,
            carName: selectedCarModel.name,
            brandName: brandName,
            baseImage: selectedCarModel.baseImage || '',
            variants: selectedVariants,
            fuelTypes: selectedFuelTypes,
            transmissions: selectedTransmissions,
            generations: generationStrings,
        };

        const updatedCompatibilities = [...selectedCompatibility];
        updatedCompatibilities[editingCompatibilityIndex] = updatedCompatibility;
        setSelectedCompatibility(updatedCompatibilities);

        // Reset all selections and close dialog
        setSelectedCarBrand(null);
        setSelectedCarModel(null);
        setSelectedVariants([]);
        setSelectedFuelTypes([]);
        setSelectedTransmissions([]);
        setSelectedGenerations([]);
        setEditDialogOpen(false);
        setEditingCompatibilityIndex(null);

        toast.success('Vehicle compatibility updated successfully!');
    };

    const handleRemoveCompatibility = (index: number) => {
        setSelectedCompatibility(prev => prev.filter((_, i) => i !== index));
    };

    // Filter functions for Autocomplete
    const filterBrands = (options: Brand[], { inputValue }: { inputValue: string }) => {
        return options.filter(option =>
            option.name.toLowerCase().includes(inputValue.toLowerCase())
        );
    };

    const filterModels = (options: CarModel[], { inputValue }: { inputValue: string }) => {
        return options.filter(option =>
            option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
            (option.brand && option.brand.name.toLowerCase().includes(inputValue.toLowerCase()))
        );
    };

    const filterCategories = (options: Category[], { inputValue }: { inputValue: string }) => {
        return options.filter(option =>
            option.name.toLowerCase().includes(inputValue.toLowerCase())
        );
    };

    const filterVendors = (options: Vendor[], { inputValue }: { inputValue: string }) => {
        return options.filter(option =>
            option.vendorName.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.nickName.toLowerCase().includes(inputValue.toLowerCase())
        );
    };

    // Prevent negative numbers in input fields
    const preventNegativeNumbers = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === '-' || e.key === 'e' || e.key === 'E') {
            e.preventDefault();
        }
    };

    const renderAttributeField = (field: AttributeField, index: number) => {
        switch (field.type) {
            case 'string':
                return (
                    <TextField
                        size="small"
                        label={field.label}
                        fullWidth
                        value={field.value || ''}
                        onChange={(e) => handleAttributeChange(index, e.target.value)}
                        required={field.required}
                    />
                );
            case 'number':
                return (
                    <TextField
                        size="small"
                        label={field.label}
                        type="number"
                        fullWidth
                        value={field.value || 0}
                        onChange={(e) => handleAttributeChange(index, Math.max(0, parseFloat(e.target.value) || 0))}
                        onKeyDown={preventNegativeNumbers}
                        inputProps={{ min: 0 }}
                        required={field.required}
                    />
                );
            case 'select':
                return (
                    <FormControl fullWidth size="small">
                        <InputLabel>{field.label}</InputLabel>
                        <Select
                            value={field.value || ''}
                            label={field.label}
                            onChange={(e) => handleAttributeChange(index, e.target.value)}
                            required={field.required}
                        >
                            {field.options.map(option => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            case 'boolean':
                return (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={field.value || false}
                                onChange={(e) => handleAttributeChange(index, e.target.checked)}
                            />
                        }
                        label={field.label}
                    />
                );
            case 'date':
                return (
                    <TextField
                        size="small"
                        label={field.label}
                        type="date"
                        fullWidth
                        value={field.value || ''}
                        onChange={(e) => handleAttributeChange(index, e.target.value)}
                        required={field.required}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                );
            default:
                return null;
        }
    };

    // Fixed cancel navigation to prevent infinite loops
    const handleCancel = () => {
        navigate('/inventory', { replace: true });
    };

    if (loadingProduct) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ mb: 2 }}>
                <Brandcrumb />
            </Box>
            <Card elevation={2} sx={{ borderRadius: 1, width: "100%", mx: 'auto' }}>
                <CardContent sx={{ p: 3 }}>
                    {/* Header Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" fontWeight={600} color="primary" gutterBottom>
                            Edit Product
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Update product information
                        </Typography>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing={3}>

                            {/* Basic Information Section with Images */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <InventoryIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Basic Information
                                    </Typography>
                                </Stack>

                                <Stack spacing={2}>
                                    {/* Product Images */}
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                            Product Images
                                        </Typography>
                                        <Stack direction="row" spacing={2} alignItems="flex-start">
                                            <Box sx={{ flex: 1 }}>
                                                <Button
                                                    variant="outlined"
                                                    component="label"
                                                    startIcon={<CloudUploadIcon />}
                                                    size="small"
                                                    fullWidth
                                                    sx={{ mb: 1 }}
                                                    disabled={isCompressing || (existingImages.length + imageFiles.length) >= 5}
                                                >
                                                    {isCompressing ? 'Compressing...' : 'Upload Images'}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        multiple
                                                        accept="image/*"
                                                        onChange={handleImageUpload}
                                                        disabled={(existingImages.length + imageFiles.length) >= 5}
                                                    />
                                                </Button>
                                                <Typography variant="caption" color="text.secondary">
                                                    Max 5 images • JPG, PNG, WebP
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ flex: 1 }}>
                                                {/* Existing Images */}
                                                {existingImages.map((image, index) => (
                                                    <Box key={`existing-${index}`} sx={{ position: 'relative', mb: 1 }}>
                                                        <Avatar
                                                            variant="rounded"
                                                            src={baseUrlImage + image}
                                                            sx={{ width: 60, height: 60 }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -8,
                                                                right: -8,
                                                                backgroundColor: 'error.main',
                                                                color: 'white',
                                                                width: 20,
                                                                height: 20,
                                                                '&:hover': {
                                                                    backgroundColor: 'error.dark',
                                                                },
                                                            }}
                                                            onClick={() => handleRemoveImage(index, 'existing')}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                                {/* New Images */}
                                                {imagePreviews.map((preview, index) => (
                                                    <Box key={`new-${index}`} sx={{ position: 'relative', mb: 1 }}>
                                                        <Avatar
                                                            variant="rounded"
                                                            src={preview}
                                                            sx={{ width: 60, height: 60 }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -8,
                                                                right: -8,
                                                                backgroundColor: 'error.main',
                                                                color: 'white',
                                                                width: 20,
                                                                height: 20,
                                                                '&:hover': {
                                                                    backgroundColor: 'error.dark',
                                                                },
                                                            }}
                                                            onClick={() => handleRemoveImage(index, 'new')}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Stack>
                                    </Box>

                                    {/* Product Details */}
                                    <Stack spacing={2}>
                                        <Stack direction="row" spacing={2}>
                                            <TextField
                                                size="small"
                                                label="Product Name *"
                                                placeholder="Enter product name"
                                                fullWidth
                                                error={!!errors.name}
                                                helperText={errors.name?.message}
                                                {...register('name', {
                                                    required: 'Product name is required',
                                                    minLength: { value: 2, message: 'Product name must be at least 2 characters' }
                                                })}
                                            />
                                            <TextField
                                                size="small"
                                                label="Part No *"
                                                placeholder="Enter Part No"
                                                fullWidth
                                                error={!!errors.partNo}
                                                helperText={errors.partNo?.message}
                                                {...register('partNo', {
                                                    required: 'Part No is required',
                                                    pattern: {
                                                        value: /^[A-Z0-9_-]+$/,
                                                        message: 'Part No must contain only uppercase letters, numbers, hyphens, and underscores'
                                                    }
                                                })}
                                            />
                                        </Stack>

                                        <Stack direction="row" spacing={2}>
                                            <TextField
                                                size="small"
                                                label="Barcode"
                                                placeholder="Enter barcode"
                                                fullWidth
                                                error={!!errors.barcode}
                                                helperText={errors.barcode?.message}
                                                {...register('barcode')}
                                            />
                                            <TextField
                                                size="small"
                                                label="Quantity *"
                                                type="number"
                                                fullWidth
                                                error={!!errors.quantity}
                                                helperText={errors.quantity?.message}
                                                onKeyDown={preventNegativeNumbers}
                                                inputProps={{ min: 0 }}
                                                {...register('quantity', {
                                                    required: 'Quantity is required',
                                                    min: { value: 0, message: 'Quantity cannot be negative' },
                                                    valueAsNumber: true,
                                                })}
                                            />
                                        </Stack>

                                        {/* Description Section */}
                                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                                <DescriptionIcon color="primary" fontSize="small" />
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    Description
                                                </Typography>
                                            </Stack>

                                            <Stack spacing={2}>
                                                {/* Description Text */}
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                                        Description Text
                                                    </Typography>
                                                    <TextField
                                                        size="small"
                                                        label="Description"
                                                        placeholder="Product description"
                                                        multiline
                                                        rows={3}
                                                        fullWidth
                                                        value={watch('description.text') || ''}
                                                        onChange={(e) => setValue('description.text', e.target.value)}
                                                    />
                                                </Box>

                                                {/* JSON Fields Section */}
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                                        Additional Information (Key-Value Pairs)
                                                    </Typography>

                                                    {/* Add new field */}
                                                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                                        <TextField
                                                            size="small"
                                                            placeholder="Key (e.g., weight, material)"
                                                            value={newJsonField.key}
                                                            onChange={(e) => setNewJsonField({ ...newJsonField, key: e.target.value })}
                                                            sx={{ flex: 1 }}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            placeholder="Value"
                                                            value={newJsonField.value}
                                                            onChange={(e) => setNewJsonField({ ...newJsonField, value: e.target.value })}
                                                            sx={{ flex: 1 }}
                                                        />
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => {
                                                                if (newJsonField.key.trim() && newJsonField.value.trim()) {
                                                                    setDescriptionJsonFields([...descriptionJsonFields, newJsonField]);
                                                                    setNewJsonField({ key: '', value: '' });
                                                                }
                                                            }}
                                                            disabled={!newJsonField.key.trim() || !newJsonField.value.trim()}
                                                        >
                                                            Add
                                                        </Button>
                                                    </Stack>

                                                    {/* Display added fields */}
                                                    {/* Display added fields as styled Chips */}
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                                        {descriptionJsonFields.map((field, index) => (
                                                            <Chip
                                                                key={index}
                                                                label={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <Typography variant="caption" >
                                                                            {field.key}:
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {field.value.length > 15 ? `${field.value.substring(0, 15)}...` : field.value}
                                                                        </Typography>
                                                                    </Box>
                                                                }
                                                                onDelete={() => {
                                                                    const updated = [...descriptionJsonFields];
                                                                    updated.splice(index, 1);
                                                                    setDescriptionJsonFields(updated);
                                                                }}
                                                                variant="outlined"
                                                                color="primary"
                                                                size="small"
                                                                sx={{
                                                                    borderRadius: 1,
                                                                    borderColor: 'primary.light',
                                                                    backgroundColor: 'primary.light + 0.04',
                                                                    '& .MuiChip-label': {
                                                                        px: 1.5,
                                                                        py: 0.75,
                                                                    },
                                                                    '&:hover': {
                                                                        backgroundColor: 'primary.light + 0.08',
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    </Stack>
                                </Stack>
                            </Paper>

                            {/* Brand & Vendor Section */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <BrandingWatermarkIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Brand & Vendor
                                    </Typography>
                                </Stack>

                                <Stack spacing={2} direction="row">
                                    <Box minWidth="50%">
                                        <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                            Product Brand *
                                        </Typography>
                                        <Controller
                                            name="brand"
                                            control={control}
                                            rules={{ required: 'Brand is required' }}
                                            render={({ field }) => (
                                                <Autocomplete
                                                    size="small"
                                                    options={partBrands}
                                                    getOptionLabel={(option) => option.name}
                                                    filterOptions={filterBrands}
                                                    value={partBrands.find(brand => brand.name === field.value) || null}
                                                    onChange={(event, newValue) => {
                                                        field.onChange(newValue ? newValue.name : '');
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Select Product Brand"
                                                            placeholder="Type to search brands..."
                                                            error={!!errors.brand}
                                                            helperText={errors.brand?.message}
                                                            disabled={loadingBrands}
                                                        />
                                                    )}
                                                    loading={loadingBrands}
                                                    noOptionsText="No brands found"
                                                />
                                            )}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            Brands with manufacturing type: Parts or Both
                                        </Typography>
                                    </Box>

                                    <Box minWidth="50%">
                                        <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                            Vendor *
                                        </Typography>
                                        <Controller
                                            name="vender"
                                            control={control}
                                            rules={{ required: 'Vendor is required' }}
                                            render={({ field }) => (
                                                <Autocomplete
                                                    size="small"
                                                    options={vendors}
                                                    getOptionLabel={(option) => option.vendorName}
                                                    filterOptions={filterVendors}
                                                    value={vendors.find(vendor => vendor.vendorName === field.value) || null}
                                                    onChange={(event, newValue) => {
                                                        field.onChange(newValue ? newValue.vendorName : '');
                                                    }}
                                                    inputValue={vendorSearchInput}
                                                    onInputChange={(event, newInputValue) => {
                                                        setVendorSearchInput(newInputValue);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Select Vendor"
                                                            placeholder="Type to search vendors..."
                                                            error={!!errors.vender}
                                                            helperText={errors.vender?.message}
                                                            disabled={loadingVendors}
                                                        />
                                                    )}
                                                    loading={loadingVendors}
                                                    noOptionsText="No vendors found"
                                                    renderOption={(props, option) => (
                                                        <li {...props}>
                                                            <Box>
                                                                <Typography variant="body2">
                                                                    {option.vendorName}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {option.nickName} • {option.contact.email}
                                                                </Typography>
                                                            </Box>
                                                        </li>
                                                    )}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Pricing Section - UPDATED with MRP and Purchase Discount */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <CurrencyRupeeIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Pricing Information (₹)
                                    </Typography>
                                </Stack>

                                <Stack direction="column" justifyContent="space-between" spacing={2}>
                                    <Box sx={{ display: "flex", gap: 2, justifyContent: 'space-between' }}>
                                        {/* MRP Price Field */}
                                        <TextField
                                            size="small"
                                            label="MRP *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.mrp}
                                            helperText={errors.mrp?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CurrencyRupeeIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('mrp', {
                                                required: 'MRP is required',
                                                min: { value: 0, message: 'MRP cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />

                                        {/* Unit Price Field */}
                                        <TextField
                                            size="small"
                                            label="Unit Price *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.unitPrice}
                                            helperText={errors.unitPrice?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CurrencyRupeeIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('unitPrice', {
                                                required: 'Unit Price is required. only 2 digits allowed after decimal',
                                                min: { value: 0, message: 'Unit Price cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 2, justifyContent: 'space-between' }}>
                                        {/* Purchase discount percentage Field */}
                                        <TextField
                                            size="small"
                                            label="Purchase Discount (%) *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.purchaseDiscount}
                                            helperText={errors.purchaseDiscount?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('purchaseDiscount', {
                                                required: 'purchaseDiscount is required',
                                                min: { value: 0, message: 'purchaseDiscount cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />

                                        {/* Purchase Price Field */}
                                        <Box sx={{ width: '100%' }}>
                                            <TextField
                                                size="small"
                                                label="Purchase Price *"
                                                type="number"
                                                fullWidth
                                                error={!!errors.purchasePrice}
                                                helperText={errors.purchasePrice?.message}
                                                onKeyDown={preventNegativeNumbers}
                                                inputProps={{ min: 0, step: "0.01" }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <CurrencyRupeeIcon color="action" fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                {...register('purchasePrice', {
                                                    required: 'Purchase price is required',
                                                    min: { value: 0, message: 'Purchase price cannot be negative' },
                                                    valueAsNumber: true,
                                                })}
                                            />
                                            <Chip label={<PriceCodeDisplay number={watch('purchasePrice')} />} />
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 2, justifyContent: 'space-between' }}>
                                        {/* B2C discount percentage Field */}
                                        <TextField
                                            size="small"
                                            label="B2C Discount (%) *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.discountB2C}
                                            helperText={errors.discountB2C?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('discountB2C', {
                                                required: 'discountB2C is required',
                                                min: { value: 0, message: 'discountB2C cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />

                                        <TextField
                                            size="small"
                                            label="Selling Price B2C *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.sellingPriceB2C}
                                            helperText={errors.sellingPriceB2C?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CurrencyRupeeIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('sellingPriceB2C', {
                                                required: 'Selling price B2C is required',
                                                min: { value: 0, message: 'Selling price B2C cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 2, justifyContent: 'space-between' }}>
                                        {/* B2B discount percentage Field */}
                                        <TextField
                                            size="small"
                                            label="B2B Discount (%) *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.discountB2B}
                                            helperText={errors.discountB2B?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('discountB2B', {
                                                required: 'discountB2B is required',
                                                min: { value: 0, message: 'discountB2B cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />

                                        <TextField
                                            size="small"
                                            label="Selling Price B2B *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.sellingPriceB2B}
                                            helperText={errors.sellingPriceB2B?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CurrencyRupeeIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('sellingPriceB2B', {
                                                required: 'Selling price B2B is required',
                                                min: { value: 0, message: 'Selling price B2B cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Vehicle Compatibility Section */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <DirectionsCarIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Vehicle Compatibility
                                    </Typography>
                                </Stack>

                                <Stack spacing={3}>
                                    {/* Brand and Model Selection with Autocomplete */}
                                    <Stack direction="row" spacing={2}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                                Select Car Brand
                                            </Typography>
                                            <Autocomplete
                                                size="small"
                                                options={carBrands}
                                                getOptionLabel={(option) => option.name}
                                                filterOptions={filterBrands}
                                                value={selectedCarBrand}
                                                onChange={(event, newValue) => {
                                                    setSelectedCarBrand(newValue);
                                                }}
                                                inputValue={brandSearchInput}
                                                onInputChange={(event, newInputValue) => {
                                                    setBrandSearchInput(newInputValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Search Car Brand"
                                                        placeholder="Type to search car brands..."
                                                        disabled={loadingBrands}
                                                    />
                                                )}
                                                loading={loadingBrands}
                                                noOptionsText="No car brands found"
                                            />
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                Brands with manufacturing type: Cars or Both
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                                Select Car Model
                                            </Typography>
                                            <Autocomplete
                                                size="small"
                                                options={filteredCarModels}
                                                getOptionLabel={(option) => option.name}
                                                filterOptions={filterModels}
                                                value={selectedCarModel}
                                                onChange={(event, newValue) => {
                                                    setSelectedCarModel(newValue);
                                                }}
                                                inputValue={modelSearchInput}
                                                onInputChange={(event, newInputValue) => {
                                                    setModelSearchInput(newInputValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Search Car Model"
                                                        placeholder="Type to search models..."
                                                        disabled={loadingModels || filteredCarModels.length === 0}
                                                    />
                                                )}
                                                loading={loadingModels}
                                                noOptionsText={selectedCarBrand ? "No models found for this brand" : "Select a car brand first"}
                                                renderOption={(props, option) => (
                                                    <li {...props}>
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {option.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {option.brand?.name}
                                                            </Typography>
                                                        </Box>
                                                    </li>
                                                )}
                                            />
                                        </Box>
                                    </Stack>

                                    {/* Car Details */}
                                    {selectedCarModel && (
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 2 }}>
                                                Configure Compatibility
                                            </Typography>

                                            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    {selectedCarModel.baseImage && (
                                                        <Avatar
                                                            variant="rounded"
                                                            src={(baseUrlImage + selectedCarModel.baseImage)}
                                                            sx={{ width: 60, height: 45 }}
                                                        />
                                                    )}
                                                    <Box>
                                                        <Typography variant="body1" fontWeight={600}>
                                                            {selectedCarModel.brand?.name} {selectedCarModel.name}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Paper>

                                            <Stack spacing={2}>
                                                <Stack direction="row" spacing={2}>
                                                    {selectedCarModel.variants.length > 0 && (
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Variants</InputLabel>
                                                            <Select
                                                                multiple
                                                                value={selectedVariants}
                                                                label="Variants"
                                                                onChange={(e) => setSelectedVariants(e.target.value as string[])}
                                                                renderValue={(selected) => (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {selected.slice(0, 2).map((value) => (
                                                                            <Chip key={value} label={value} size="small" />
                                                                        ))}
                                                                        {selected.length > 2 && (
                                                                            <Chip label={`+${selected.length - 2}`} size="small" />
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            >
                                                                {selectedCarModel.variants.map(variant => (
                                                                    <MenuItem key={variant} value={variant}>
                                                                        <Checkbox checked={selectedVariants.indexOf(variant) > -1} />
                                                                        {variant}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                    {selectedCarModel.fuelTypes.length > 0 && (
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Fuel Types</InputLabel>
                                                            <Select
                                                                multiple
                                                                value={selectedFuelTypes}
                                                                label="Fuel Types"
                                                                onChange={(e) => setSelectedFuelTypes(e.target.value as string[])}
                                                                renderValue={(selected) => (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {selected.map((value) => (
                                                                            <Chip key={value} label={value} size="small" />
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            >
                                                                {selectedCarModel.fuelTypes.map(fuelType => (
                                                                    <MenuItem key={fuelType} value={fuelType}>
                                                                        <Checkbox checked={selectedFuelTypes.indexOf(fuelType) > -1} />
                                                                        {fuelType}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </Stack>
                                                <Stack direction="row" spacing={2}>
                                                    {selectedCarModel.transmissions.length > 0 && (
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Transmissions</InputLabel>
                                                            <Select
                                                                multiple
                                                                value={selectedTransmissions}
                                                                label="Transmissions"
                                                                onChange={(e) => setSelectedTransmissions(e.target.value as string[])}
                                                                renderValue={(selected) => (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {selected.map((value) => (
                                                                            <Chip key={value} label={value} size="small" />
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            >
                                                                {selectedCarModel.transmissions.map(transmission => (
                                                                    <MenuItem key={transmission} value={transmission}>
                                                                        <Checkbox checked={selectedTransmissions.indexOf(transmission) > -1} />
                                                                        {transmission}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                    {selectedCarModel.generations.length > 0 && (
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Generations</InputLabel>
                                                            <Select
                                                                multiple
                                                                value={selectedGenerations}
                                                                label="Generations"
                                                                onChange={(e) => setSelectedGenerations(e.target.value as string[])}
                                                                renderValue={(selected) => (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {selected.slice(0, 2).map((value) => (
                                                                            <Chip key={value} label={value} size="small" />
                                                                        ))}
                                                                        {selected.length > 2 && (
                                                                            <Chip label={`+${selected.length - 2}`} size="small" />
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            >
                                                                {selectedCarModel.generations.map((gen, index) => (
                                                                    <MenuItem key={index} value={`${gen.from}-${gen.to}`}>
                                                                        <Checkbox checked={selectedGenerations.indexOf(`${gen.from}-${gen.to}`) > -1} />
                                                                        {gen.from} - {gen.to}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </Stack>
                                            </Stack>

                                            <Box sx={{ pt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AddIcon />}
                                                    onClick={handleAddCompatibility}
                                                    disabled={!selectedCarModel}
                                                    fullWidth
                                                    size="small"
                                                >
                                                    Add Vehicle Compatibility
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Selected Compatibilities */}
                                    {selectedCompatibility.length > 0 && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 2 }}>
                                                Added Compatibilities ({selectedCompatibility.length})
                                            </Typography>
                                            <Stack spacing={2}>
                                                {selectedCompatibility.map((comp, index) => (
                                                    <Paper
                                                        key={index}
                                                        variant="outlined"
                                                        sx={{
                                                            p: 2,
                                                            position: 'relative',
                                                            borderColor: 'success.light',
                                                            bgcolor: 'success.light + 0.04'
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={2} alignItems="flex-start">
                                                            {comp.baseImage && (
                                                                <Avatar
                                                                    variant="rounded"
                                                                    src={(baseUrlImage + comp.baseImage)}
                                                                    sx={{ width: 50, height: 38 }}
                                                                />
                                                            )}
                                                            <Box sx={{ flex: 1 }}>
                                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                                    <Box>
                                                                        <Typography variant="body1" fontWeight={600} fontSize="0.9rem">
                                                                            {comp.brandName} {comp.carName}
                                                                        </Typography>

                                                                        {/* Detailed Compatibility Information */}
                                                                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                                                                            {comp.variants.length > 0 && (
                                                                                <Box>
                                                                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                                                        Variants:
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                                                                        {comp.variants.map((variant, i) => (
                                                                                            <Chip
                                                                                                key={i}
                                                                                                label={variant}
                                                                                                size="small"
                                                                                                color="primary"
                                                                                                variant="outlined"
                                                                                            />
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}

                                                                            {comp.fuelTypes.length > 0 && (
                                                                                <Box>
                                                                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                                                        Fuel Types:
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                                                                        {comp.fuelTypes.map((fuelType, i) => (
                                                                                            <Chip
                                                                                                key={i}
                                                                                                label={fuelType}
                                                                                                size="small"
                                                                                                color="success"
                                                                                                variant="outlined"
                                                                                            />
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}

                                                                            {comp.transmissions.length > 0 && (
                                                                                <Box>
                                                                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                                                        Transmissions:
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                                                                        {comp.transmissions.map((transmission, i) => (
                                                                                            <Chip
                                                                                                key={i}
                                                                                                label={transmission}
                                                                                                size="small"
                                                                                                color="info"
                                                                                                variant="outlined"
                                                                                            />
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}

                                                                            {comp.generations.length > 0 && (
                                                                                <Box>
                                                                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                                                        Generations:
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                                                                        {comp.generations.map((generation, i) => (
                                                                                            <Chip
                                                                                                key={i}
                                                                                                label={generation}
                                                                                                size="small"
                                                                                                color="warning"
                                                                                                variant="outlined"
                                                                                            />
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}
                                                                        </Stack>
                                                                    </Box>
                                                                    <Stack direction="row" spacing={0.5}>
                                                                        <Tooltip title="Edit compatibility">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleEditCompatibility(index)}
                                                                                sx={{
                                                                                    color: 'primary.main',
                                                                                    '&:hover': { backgroundColor: 'primary.light' }
                                                                                }}
                                                                            >
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Remove compatibility">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleRemoveCompatibility(index)}
                                                                                sx={{
                                                                                    color: 'error.main',
                                                                                    '&:hover': { backgroundColor: 'error.light' }
                                                                                }}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Stack>
                                                                </Stack>
                                                            </Box>
                                                        </Stack>
                                                    </Paper>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}

                                    {/* Empty State */}
                                    {selectedCompatibility.length === 0 && !selectedCarBrand && (
                                        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                                            <DirectionsCarIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                                            <Typography variant="h6" color="text.secondary" gutterBottom fontSize="1rem">
                                                No Vehicle Compatibility Added
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                                                Start by selecting a car brand to add vehicle compatibility
                                            </Typography>
                                        </Paper>
                                    )}
                                </Stack>
                            </Paper>

                            {/* Category & Status Section */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <CategoryIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Category & Status
                                    </Typography>
                                </Stack>

                                <Stack direction="row" spacing={2}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                            Select Category
                                        </Typography>
                                        <Controller
                                            name="category"
                                            control={control}
                                            rules={{ required: 'Category is required' }}
                                            render={({ field }) => (
                                                <Autocomplete
                                                    size="small"
                                                    options={categories}
                                                    getOptionLabel={(option) => option.name}
                                                    filterOptions={filterCategories}
                                                    value={categories.find(cat => cat.name === field.value) || null}
                                                    onChange={(event, newValue) => {
                                                        field.onChange(newValue ? newValue.name : '');
                                                    }}
                                                    inputValue={categorySearchInput}
                                                    onInputChange={(event, newInputValue) => {
                                                        setCategorySearchInput(newInputValue);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Search Category"
                                                            placeholder="Type to search categories..."
                                                            error={!!errors.category}
                                                            helperText={errors.category?.message}
                                                        />
                                                    )}
                                                    noOptionsText="No categories found"
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                            Select Status
                                        </Typography>
                                        <FormControl size="small" fullWidth>
                                            <InputLabel>Status</InputLabel>
                                            <Controller
                                                name="status"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select {...field} label="Status">
                                                        <MenuItem value="active">Active</MenuItem>
                                                        <MenuItem value="inactive">Inactive</MenuItem>
                                                    </Select>
                                                )}
                                            />
                                        </FormControl>
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Source & Import Information Section (Read-only/Display only) */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#fafafa' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <InventoryIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Source & Import Information
                                    </Typography>
                                    <Chip
                                        label="Read Only"
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                </Stack>

                                <Stack spacing={2}>
                                    <Stack direction="row" spacing={2}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                                                Source Type
                                            </Typography>
                                            <Chip
                                                label={sourceType}
                                                color={
                                                    sourceType === 'manual' ? 'default' :
                                                        sourceType === 'price-list' ? 'primary' :
                                                            sourceType === 'import' ? 'secondary' : 'warning'
                                                }
                                                variant="filled"
                                                sx={{ textTransform: 'capitalize' }}
                                            />
                                        </Box>

                                        {sourceId && (
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                                                    Source ID
                                                </Typography>
                                                <TextField
                                                    size="small"
                                                    value={sourceId}
                                                    fullWidth
                                                    disabled
                                                    InputProps={{
                                                        readOnly: true,
                                                    }}
                                                />
                                            </Box>
                                        )}

                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                                                Import Batch ID
                                            </Typography>
                                            <TextField
                                                size="small"
                                                value={importBatchId}
                                                fullWidth
                                                disabled
                                                InputProps={{
                                                    readOnly: true,
                                                }}
                                            />
                                        </Box>
                                    </Stack>



                                    {/* Source Metadata Display */}
                                    {sourceMetadata.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                                                Source Metadata
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                                {sourceMetadata.map((field, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant="caption">
                                                                {field.key}:
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {field.value.length > 15 ? `${field.value.substring(0, 15)}...` : field.value}
                                                            </Typography>
                                                        </Box>}
                                                        color="secondary"
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{
                                                            '& .MuiChip-label': {
                                                                fontSize: '0.75rem',
                                                                px: 1,
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            <em>Note: Source information cannot be modified as it tracks the origin of this product entry.</em>
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Category-specific Attributes */}
                            {attributeFields.length > 0 && (
                                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                        <CategoryIcon color="primary" fontSize="small" />
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {selectedCategory?.name} Attributes
                                        </Typography>
                                    </Stack>

                                    <Stack spacing={2}>
                                        {attributeFields.map((field, index) => (
                                            <Box key={field.key}>
                                                {renderAttributeField(field, index)}
                                            </Box>
                                        ))}
                                    </Stack>
                                </Paper>
                            )}

                            {/* Action Buttons */}
                            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancel}
                                    size="small"
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    sx={{ minWidth: 100 }}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={20} /> : 'Update Product'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </CardContent>

                {/* Edit Compatibility Dialog */}
                <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ pb: 1 }}>
                        <Typography variant="h6">Edit Vehicle Compatibility</Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Car Brand</InputLabel>
                                    <Select
                                        value={selectedCarBrand?._id || ''}
                                        label="Car Brand"
                                        onChange={(e) => {
                                            const brand = carBrands.find(b => b._id === e.target.value);
                                            setSelectedCarBrand(brand || null);
                                        }}
                                    >
                                        {carBrands.map(brand => (
                                            <MenuItem key={brand._id} value={brand._id}>
                                                {brand.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Car Model</InputLabel>
                                    <Select
                                        value={selectedCarModel?._id || ''}
                                        label="Car Model"
                                        onChange={(e) => {
                                            const model = filteredCarModels.find(m => m._id === e.target.value);
                                            setSelectedCarModel(model || null);
                                        }}
                                    >
                                        {filteredCarModels.map(model => (
                                            <MenuItem key={model._id} value={model._id}>
                                                {model.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>

                            {selectedCarModel && (
                                <Stack spacing={2}>
                                    <Stack direction="row" spacing={2}>
                                        {selectedCarModel.variants.length > 0 && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Variants</InputLabel>
                                                <Select
                                                    multiple
                                                    value={selectedVariants}
                                                    label="Variants"
                                                    onChange={(e) => setSelectedVariants(e.target.value as string[])}
                                                >
                                                    {selectedCarModel.variants.map(variant => (
                                                        <MenuItem key={variant} value={variant}>
                                                            <Checkbox checked={selectedVariants.indexOf(variant) > -1} />
                                                            {variant}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                        {selectedCarModel.fuelTypes.length > 0 && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Fuel Types</InputLabel>
                                                <Select
                                                    multiple
                                                    value={selectedFuelTypes}
                                                    label="Fuel Types"
                                                    onChange={(e) => setSelectedFuelTypes(e.target.value as string[])}
                                                >
                                                    {selectedCarModel.fuelTypes.map(fuelType => (
                                                        <MenuItem key={fuelType} value={fuelType}>
                                                            <Checkbox checked={selectedFuelTypes.indexOf(fuelType) > -1} />
                                                            {fuelType}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        {selectedCarModel.transmissions.length > 0 && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Transmissions</InputLabel>
                                                <Select
                                                    multiple
                                                    value={selectedTransmissions}
                                                    label="Transmissions"
                                                    onChange={(e) => setSelectedTransmissions(e.target.value as string[])}
                                                >
                                                    {selectedCarModel.transmissions.map(transmission => (
                                                        <MenuItem key={transmission} value={transmission}>
                                                            <Checkbox checked={selectedTransmissions.indexOf(transmission) > -1} />
                                                            {transmission}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                        {selectedCarModel.generations.length > 0 && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Generations</InputLabel>
                                                <Select
                                                    multiple
                                                    value={selectedGenerations}
                                                    label="Generations"
                                                    onChange={(e) => setSelectedGenerations(e.target.value as string[])}
                                                >
                                                    {selectedCarModel.generations.map((gen, index) => (
                                                        <MenuItem key={index} value={`${gen.from}-${gen.to}`}>
                                                            <Checkbox checked={selectedGenerations.indexOf(`${gen.from}-${gen.to}`) > -1} />
                                                            {gen.from} - {gen.to}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setEditDialogOpen(false)} size="small">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpdateCompatibility}
                            variant="contained"
                            disabled={!selectedCarModel}
                            size="small"
                        >
                            Update
                        </Button>
                    </DialogActions>
                </Dialog>
            </Card>
        </>
    );
};

export default EditProduct;

